'use client';

import React, { useState, useRef } from 'react';
import { Alert } from 'antd';
import { FaEnvelope, FaUser, FaPaperPlane } from 'react-icons/fa6';
import { MdSubject } from 'react-icons/md';

type AlertType = 'success' | 'info' | 'warning' | 'error';

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertType, setAlertType] = useState<AlertType>('info');
  const [alertMessage, setAlertMessage] = useState(''); // Title
  const [alertDescription, setAlertDescription] = useState(''); // Body
  const alertRef = useRef<HTMLDivElement | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // prevent full page refresh
    setAlertVisible(false); // reset previous alert
    setIsSubmitting(true);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));

    // Since there are no APIs yet, always show an error alert
    setAlertType('error');
    setAlertMessage('Request Failed');
    setAlertDescription(
      'Unable to perform this request at the moment, please try again later.'
    );
    setAlertVisible(true);
    setIsSubmitting(false);

    // Scroll to the alert for visibility (optional)
    setTimeout(() => {
      alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  return (
    <div className="bg-white border border-[#4d6b57] rounded-md p-6 md:p-8 shadow-sm justify-center items-center">
      <h2 className="text-2xl font-semibold text-[#2f4f3f]">
        Send us a message
      </h2>
      <p className="text-sm text-[#4d6b57] mb-6">
        Please fill out the form below and we will get back to you as soon as
        possible.
      </p>

      <form className="space-y-6 my-auto" onSubmit={handleSubmit}>
        {/* Name + Email */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center border border-[#4d6b57] rounded px-3">
            <FaUser className="text-[#4d6b57] mr-2 shrink-0" />
            <input
              type="text"
              placeholder="Your name"
              className="w-full py-2 focus:outline-none"
              aria-label="Your name"
              required
            />
          </div>

          <div className="flex items-center border border-[#4d6b57] rounded px-3">
            <FaEnvelope className="text-[#4d6b57] mr-2 shrink-0" />
            <input
              type="email"
              placeholder="Your email"
              className="w-full py-2 focus:outline-none"
              aria-label="Your email"
              required
            />
          </div>
        </div>

        {/* Subject */}
        <div className="flex items-center border border-[#4d6b57] rounded px-3">
          <MdSubject className="text-[#4d6b57] mr-2 shrink-0" />
          <input
            type="text"
            placeholder="Subject"
            className="w-full py-2 focus:outline-none"
            aria-label="Subject"
            required
          />
        </div>

        {/* Message */}
        <div className="flex items-start border border-[#4d6b57] rounded px-3">
          <FaEnvelope className="text-[#4d6b57] mr-2 mt-3 shrink-0" />
          <textarea
            placeholder="Enter message"
            rows={5}
            className="w-full py-2 focus:outline-none resize-y"
            aria-label="Message"
            required
          />
        </div>

        {/* Submit button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full sm:w-auto mx-auto sm:mx-0 flex items-center justify-center gap-2 px-6 py-2 rounded shadow border transition 
            ${
              isSubmitting
                ? 'bg-[#4d6b57] text-white'
                : 'bg-white border-[#4d6b57] text-[#4d6b57] hover:bg-[#4d6b57] hover:text-white'
            }`}
        >
          <FaPaperPlane className={isSubmitting ? 'animate-pulse' : ''} />
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </button>

        {/* Alert Strip (above form) */}
        <div ref={alertRef} className="mb-4">
          {alertVisible && (
            <Alert
              type={alertType}
            //   message={alertMessage}
              description={alertDescription}
              showIcon
              closable
              onClose={() => setAlertVisible(false)}
            />
          )}
        </div>
      </form>
    </div>
  );
}
